import { useEffect, useRef } from "react";
import type { Map as LeafletMap } from "leaflet";
import { metres, type Visit } from "@/lib/visits";
import { formatTime } from "@/lib/dateTime";

export interface MapPoint extends Visit {
  seq: number;
}
export interface MapCustomer {
  visit_id: string;
  related_id: string;
  label: string;
  lat: number;
  lng: number;
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
const PIN = { OK: "#16a34a", NO_CUSTOMER_LOCATION: "#64748b", LOW_ACCURACY: "#d97706", FAR: "#dc2626" } as Record<string, string>;

/**
 * One rep's day on OpenStreetMap: numbered check-ins in the order they were made (red when far from the customer), a line
 * between them, and each customer's saved location as a small square. Leaflet loads only when this is shown.
 */
export function VisitMap({ points, customers, onSelect }: { points: MapPoint[]; customers: MapCustomer[]; onSelect?: (id: string) => void }) {
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<LeafletMap | null>(null);

  useEffect(() => {
    let cancelled = false;
    void import("leaflet").then((mod) => {
      const L = (mod as any).default || mod;
      if (cancelled || !el.current) return;
      if (!map.current) {
        map.current = L.map(el.current, { zoomControl: true, attributionControl: true });
        L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "&copy; OpenStreetMap contributors" }).addTo(map.current);
      }
      const m = map.current as LeafletMap;
      m.eachLayer((layer: any) => {
        if (!layer.getAttribution?.()) m.removeLayer(layer);
      });
      const bounds: [number, number][] = [];
      for (const c of customers) {
        bounds.push([c.lat, c.lng]);
        L.marker([c.lat, c.lng], {
          icon: L.divIcon({ className: "", html: `<span style="display:block;width:12px;height:12px;border:2px solid #fff;background:#2563eb;box-shadow:0 0 0 1px #2563eb"></span>`, iconSize: [12, 12], iconAnchor: [6, 6] }),
          title: `${c.label} (saved location)`,
        })
          .bindTooltip(`${esc(c.label)} · saved location`)
          .addTo(m);
      }
      const line: [number, number][] = [];
      for (const p of points) {
        if (p.check_in_lat == null || p.check_in_lng == null) continue;
        const at: [number, number] = [p.check_in_lat, p.check_in_lng];
        bounds.push(at);
        line.push(at);
        const colour = PIN[p.location_flag || "OK"] || PIN.OK;
        const time = p.check_in_at ? formatTime(p.check_in_at) : "";
        L.marker(at, {
          icon: L.divIcon({
            className: "",
            html: `<span style="display:flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:9999px;background:${colour};color:#fff;font:600 12px system-ui;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35)">${p.seq}</span>`,
            iconSize: [26, 26],
            iconAnchor: [13, 13],
          }),
          title: p.related_label,
        })
          .bindTooltip(`${p.seq}. ${esc(p.related_label)} · ${time}${p.check_in_distance_m != null ? ` · ${metres(p.check_in_distance_m)} away` : ""}`)
          .on("click", () => onSelect?.(p.id))
          .addTo(m);
      }
      if (line.length > 1) L.polyline(line, { color: "#475569", weight: 2, opacity: 0.6, dashArray: "4 6" }).addTo(m);
      if (bounds.length) m.fitBounds(bounds, { padding: [32, 32], maxZoom: 16 });
      else m.setView([20.6, 78.9], 4);
    });
    return () => {
      cancelled = true;
    };
  }, [points, customers]);

  useEffect(
    () => () => {
      map.current?.remove();
      map.current = null;
    },
    [],
  );

  return <div ref={el} className="h-[420px] w-full overflow-hidden rounded-lg border border-border md:h-[520px]" role="region" aria-label="Map of the day's visits" />;
}
